// external imports
import { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

// hooks & services
import useGainsQCApi from '../services/useGainsQCApi';
import { endpointContext } from '../contexts/endpointContext';
import { capFirstLetter } from '../components/qc_steps/helpers';

// ideas
// filter by deleted comments, raised by, site ID, status
// link to view site
// view most recent captures for that site

export const AdminPage = (props) => {
	const [commentType, setCommentType] = useState('site'); // site, capture
	const [commodityType, setCommodityType] = useState('steel'); // steel, titanium
	const endpointContextAdminPage = useContext(endpointContext);

	const [resolvedTableData, setResolvedTableData] = useState([]);
	const [unResolvedTableData, setUnresolvedTableData] = useState([]);

	const resolvedComments = useGainsQCApi();
	const unresolvedComments = useGainsQCApi();
	const changeCommentStatus = useGainsQCApi();

	const COMMENT_API_ALLOWED_COMMODITIES = ['copper', 'nickel', 'steel'];
	const TABLE_COLS = [
		'Raised at',
		'Site ID',
		'Raised by',
		'Category',
		'Comment',
		'Labels',
		'Status'
	];

	const rows = 3;
	const columns = TABLE_COLS.length;

	useEffect(() => {
		resolvedComments.resetData();
		unresolvedComments.resetData();
		setResolvedTableData([]);
		setUnresolvedTableData([]);
		fetchComments();
	}, [commentType, commodityType]);

	useEffect(() => {
		if (resolvedComments?.data?.length > 0) {
			setResolvedTableData(resolvedComments?.data);
		}

		if (unresolvedComments?.data?.length > 0) {
			setUnresolvedTableData(unresolvedComments?.data);
		}
	}, [resolvedComments.data, unresolvedComments.data]);

	const fetchComments = async () => {
		const unResolvedQueryString =
			commentType === 'site'
				? 'site?status_id=1'
				: `${commodityType}?status_id=1`;
		const resolvedQueryString =
			commentType === 'site'
				? 'site?status_id=2'
				: `${commodityType}?status_id=2`;

		await Promise.all([
			resolvedComments.makeRequest(
				`/smelters/comments/${resolvedQueryString}`,
				'GET',
				null,
				'unresolved comments',
				500
			),
			unresolvedComments.makeRequest(
				`/smelters/comments/${unResolvedQueryString}`,
				'GET',
				null,
				'resolved comments',
				500
			)
		]);
	};

	const changingCommentStatus = async (commentId, newStatusId) => {
		const urlStr = commentType === 'site' ? commentType : commodityType;

		await changeCommentStatus.makeRequest(
			`/smelters/comments/${urlStr}/${commentId}/status`,
			'PATCH',
			{ status_id: newStatusId },
			'new comment status'
		);
	};

	const handleChangeCommentStatus = async (commentId, newStatusId) => {
		await changingCommentStatus(commentId, newStatusId);
		await fetchComments();
	};

	const renderCommentTypePills = () => {
		return (
			<>
				<div className="d-flex justify-content-start mt-3">
					<h4>Comment Type</h4>
					<div className="d-flex align-items-center ms-3">
						<div
							class="form-check-dashboard-qcinfo"
							data-tooltip-id="dashboard-qc-type-radios-smelter"
						>
							<input
								className="form-check-input-dashboard-qcinfo"
								type="radio"
								name="flexRadioDefault"
								id="flexRadioDefault1"
								checked={commentType === 'site'}
								onChange={() => setCommentType('site')}
							></input>
							<label
								class="form-check-label-dashboard-qcinfo"
								for="flexRadioDefault1"
								data-tooltip-id="dashboard-qcinfo-sites"
							>
								<i className="fa-solid fa-city"></i>
							</label>
						</div>
						<div
							className="form-check-dashboard-qcinfo"
							data-tooltip-id="dashboard-qc-type-radios-stockpiles"
						>
							<input
								className="form-check-input-dashboard-qcinfo"
								type="radio"
								name="flexRadioDefault"
								checked={commentType === 'capture'}
								id="flexRadioDefault2"
								onChange={(e) => setCommentType('capture')}
							></input>
							<label
								className="form-check-label-dashboard-qcinfo"
								for="flexRadioDefault2"
								data-tooltip-id="dashboard-qcinfo-capture"
							>
								<i className="fa-solid fa-images"></i>
							</label>
						</div>
					</div>

					<Tooltip id="dashboard-qcinfo-sites" place="bottom" effect="solid">
						View site comments
					</Tooltip>

					<Tooltip id="dashboard-qcinfo-capture" place="bottom" effect="solid">
						View capture comments
					</Tooltip>
				</div>
			</>
		);
	};

	const renderCommodityTypePills = () => {
		return (
			<>
				<div className="d-flex justify-content-start mt-3">
					<h4>Commodity</h4>
					<div className="d-flex align-items-center ms-3">
						{endpointContextAdminPage?.dbData?.allCommodities
							?.filter((comm) =>
								COMMENT_API_ALLOWED_COMMODITIES.includes(comm.name)
							)
							?.map((comm, idx) => (
								<>
									<div
										className="form-check-dashboard-qcinfo"
										data-tooltip-id={`admin-page-commodities-${comm.name}`}
									>
										<input
											className="form-check-input-dashboard-qcinfo"
											type="radio"
											name={`admin-radio-commodity`}
											id={`admin-radio-commodity-${comm.name}`}
											checked={commodityType === comm.name}
											onChange={() => setCommodityType(comm.name)}
										></input>
										<label
											class="form-check-label-dashboard-qcinfo"
											for={`admin-radio-commodity-${comm.name}`}
											data-tooltip-id={`admin-page-commodities-${comm.name}`}
										>
											{comm.name}
										</label>
									</div>

									<Tooltip
										id={`admin-page-commodities-${comm.name}`}
										place="bottom"
										effect="solid"
									>
										{comm.name}
									</Tooltip>
								</>
							))}
					</div>
				</div>
			</>
		);
	};

	const renderTable = (data) => {
		return (
			<div className="table-responsive mt-2" style={{ maxHeight: '500px' }}>
				<table className="captureTable">
					<thead className="sticky-thead">
						<tr>
							{TABLE_COLS.map((col, idx) => (
								<th key={idx}>{col}</th>
							))}
						</tr>
					</thead>

					{data.length === 0 ? (
						<tbody>
							{Array.from({ length: rows }).map((_, rowIndex) => (
								<tr key={rowIndex} height={40}>
									{Array.from({ length: columns }).map((_, colIndex) => (
										<td key={colIndex}></td>
									))}
								</tr>
							))}
						</tbody>
					) : (
						<tbody>
							{data?.map((comment, idx) => (
								<tr key={idx}>
									<td>{comment.created.slice(0, 10)}</td>
									<td>{comment.subject_id}</td>
									<td>{comment.user_id}</td>
									<td>
										{
											endpointContextAdminPage.dbData?.commentCategories?.find(
												(cat) => cat.id === comment.category_id
											)?.name
										}
									</td>
									<td style={{ textAlign: 'left' }}>{comment.content.text}</td>
									<td style={{ textAlign: 'left' }}>{comment.labels}</td>
									<td>
										<select
											name="comment_status"
											id="comment_status"
											className="form-control"
											value={comment.status_id || ''}
											onChange={(e) =>
												handleChangeCommentStatus(
													comment.id,
													parseInt(e.target.value)
												)
											}
											data-tooltip-id="change-comment-status-TT"
										>
											{endpointContextAdminPage.dbData?.commentStatuses?.map(
												(cat) => (
													<option key={cat.id} value={cat.id}>
														{cat.name}
													</option>
												)
											)}
										</select>

										<Tooltip
											id={`change-comment-status-TT`}
											place="top"
											effect="solid"
										>
											Change the status of this comment
										</Tooltip>
									</td>
								</tr>
							))}
						</tbody>
					)}
				</table>
			</div>
		);
	};

	return (
		<>
			{props.authProps.isAuthenticated ? (
				<>
					<div className="col px-0">
						<div className="contentContainer">
							<hr style={{ marginTop: '58px' }}></hr>
							<h2>Admin Dashboard</h2>

							<br></br>

							{renderCommentTypePills()}

							{commentType === 'capture' && renderCommodityTypePills()}

							<h5 className="mt-5">
								Unresolved {capFirstLetter(commentType)} Comments (
								{unResolvedTableData?.length})
							</h5>

							{renderTable(unResolvedTableData)}

							<h5 className="mt-5">
								Resolved {capFirstLetter(commentType)} Comments (
								{resolvedTableData?.length})
							</h5>

							{renderTable(resolvedTableData)}
						</div>
					</div>
				</>
			) : (
				<Navigate to="/login" />
			)}
		</>
	);
};
